import { formatShortDateTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import Channel from "@sendbird/uikit-react/Channel";
import sendbirdSelectors from "@sendbird/uikit-react/sendbirdSelectors";
// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line import/no-named-as-default
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { ChatPageWrapper } from "@src/appV2/Chat/ChatPageWrapper";
import { CHAT_MESSAGE_SPACING } from "@src/appV2/Chat/constants";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { PageHeaderWrapper } from "@src/appV2/redesign/system/ui-components";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { ChatHeader } from "../Chat/ChatHeader";
import { ChatSeparator } from "../Chat/ChatSeparator";
import { MessageContentWithLongPress } from "../Chat/components/MessageContentWithLongPress";
import { EditMessageProvider } from "../Chat/context/EditMessageContext";
import { ChannelDescriptionBanner } from "./components/ChannelDescriptionBanner";
import { ClipboardGuidesChatHeaderTitle } from "./components/ClipboardGuidesChatHeaderTitle";
import { LeaveClipboardGuidesChannelButton } from "./components/LeaveClipboardGuidesChannelButton";

interface ClipboardGuidesChatLocationState {
  channelUrl?: string;
  workplaceName?: string;
}

interface ClipboardGuidesChatRouteParams {
  workplaceId: string;
}

export function ClipboardGuidesChatPage(): JSX.Element {
  const { workplaceId } = useParams<ClipboardGuidesChatRouteParams>();
  const location = useLocation<ClipboardGuidesChatLocationState>();
  const history = useHistory();
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendbirdSelectors.getSdk(sendBirdState);
  const isSdkReady = isDefined(sdk?.groupChannel);

  const clipboardGuidesConfig = useCbhFlag(CbhFeatureFlag.ALCHEMY_CLIPBOARD_GUIDES_EXPERIMENT, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ALCHEMY_CLIPBOARD_GUIDES_EXPERIMENT],
  });

  const channelUrl =
    location.state?.channelUrl ?? `${workplaceId}_${clipboardGuidesConfig.channelUrlSuffix}`;
  const workplaceNameFromState = location.state?.workplaceName;

  return (
    <EditMessageProvider>
      <ChatPageWrapper>
        {isSdkReady && (
          <Channel
            isReactionEnabled={false}
            channelUrl={channelUrl}
            renderChannelHeader={() => (
              <>
                <PageHeaderWrapper
                  variant="tertiary"
                  sx={{ boxShadow: (theme) => theme.shadows[2] }}
                >
                  {isDefined(workplaceNameFromState) ? (
                    <ChatHeader
                      title={`Community at ${workplaceNameFromState}`}
                      subtitle="Ask the regulars"
                      rightAction={
                        <LeaveClipboardGuidesChannelButton
                          workplaceId={workplaceId}
                          onLeft={() => {
                            history.goBack();
                          }}
                        />
                      }
                      onBack={() => {
                        history.goBack();
                      }}
                    />
                  ) : (
                    <ClipboardGuidesChatHeaderTitle
                      rightAction={
                        <LeaveClipboardGuidesChannelButton
                          workplaceId={workplaceId}
                          onLeft={() => {
                            history.goBack();
                          }}
                        />
                      }
                      onBack={() => {
                        history.goBack();
                      }}
                    />
                  )}
                </PageHeaderWrapper>
                <ChannelDescriptionBanner />
              </>
            )}
            renderCustomSeparator={({ message }) => (
              <ChatSeparator sx={{ marginY: CHAT_MESSAGE_SPACING }}>
                {formatShortDateTime(new Date(message.createdAt))}
              </ChatSeparator>
            )}
            renderMessageContent={(contentProps) => (
              <MessageContentWithLongPress
                message={contentProps.message}
                channel={contentProps.channel!}
                userId={contentProps.userId}
                chainTop={contentProps.chainTop}
                chainBottom={contentProps.chainBottom}
              />
            )}
          />
        )}
      </ChatPageWrapper>
    </EditMessageProvider>
  );
}
